import { Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import { Role } from "data/models";

interface UserCardProps {
  name: string;
  email: string;
  status: string;
  added: string;
}

interface RoleSelectorProps {
  memberRole: Role;
  onSubmit: {
    mutate: (
      variables: { role: string },
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      },
    ) => void;
  };
}

type RoleSelectorFormValues = {
  selectedRole: Role;
};

export const RoleSelector = ({ memberRole, onSubmit }: RoleSelectorProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const roles = [
    {
      value: Role.VIEWER,
      label: "Viewer",
      description: "Read-only access.",
    },
    {
      value: Role.ANALYST,
      label: "Analyst",
      description: "Technical user with access to Datasets and Clip.",
    },
    {
      value: Role.DEVELOPER,
      label: "Developer",
      description:
        "Technical user with access to development-related features.",
    },
    {
      value: Role.BILLING,
      label: "Billing",
      description: "Access to billing and subscription management.",
    },
    {
      value: Role.ADMIN,
      label: "Admin",
      description: "Full control over the organisation.",
    },
    ...(memberRole === Role.OWNER
      ? [
          {
            value: Role.OWNER,
            label: "Owner",
            description: "Full control over the organisation.",
          },
        ]
      : []),
  ];

  const handleSubmit = (
    values: RoleSelectorFormValues,
    { setSubmitting }: FormikHelpers<RoleSelectorFormValues>,
  ) => {
    onSubmit.mutate(
      { role: values.selectedRole },
      {
        onSuccess: () => {
          setSubmitting(false);
          setIsEditing(false);
        },
        onError: () => {
          setSubmitting(false);
        },
      },
    );
  };

  const handleCancel = (resetForm: () => void) => {
    resetForm();
    setIsEditing(false);
  };

  return (
    <Card className="w-full">
      <Formik<RoleSelectorFormValues>
        enableReinitialize
        initialValues={{ selectedRole: memberRole }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, resetForm, isSubmitting }) => (
          <Form>
            <CardHeader>
              <CardTitle>Role</CardTitle>
              <CardDescription>
                Manage the permissions of this member in the organisation by
                their role.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={values.selectedRole}
                onValueChange={(value) => {
                  if (isEditing) {
                    setFieldValue("selectedRole", value);
                  }
                }}
                className="gap-3"
              >
                {roles.map((role) => (
                  <div key={role.value} className="flex items-start gap-3">
                    <RadioGroupItem
                      value={role.value}
                      id={`role-${role.value}`}
                      disabled={!isEditing}
                      className="mt-1"
                    />
                    <Label
                      htmlFor={`role-${role.value}`}
                      className="flex flex-col items-start gap-0.5 font-normal"
                    >
                      <span className="text-sm">{role.label}</span>
                      <span className="text-muted-foreground text-xs">
                        {role.description}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <PermissionsWrapper
                keys={[PermissionsKeys.rolesUpdate]}
                messages={{
                  [PermissionsReasons.ROLE_DENY]: <></>,
                  [PermissionsReasons.UPGRADE_REQUIRED]: (
                    <Alert variant="info" className="mt-4">
                      <AlertTitle>Organise Your Members with Roles</AlertTitle>
                      <AlertDescription>
                        Unlock powerful member management capabilities with
                        Roles, available exclusively on our Professional and
                        Enterprise plans.
                      </AlertDescription>
                      <AlertAction>
                        <UpgradeSubscriptionButton />
                      </AlertAction>
                    </Alert>
                  ),
                }}
              >
                <></>
              </PermissionsWrapper>
            </CardContent>

            <CardFooter className="justify-end gap-2">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleCancel(resetForm)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : memberRole === Role.OWNER ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <PermissionTooltipButton
                          permissionKeys={[PermissionsKeys.rolesUpdate]}
                          onClick={() => setIsEditing(true)}
                          isDisabled
                        >
                          Edit
                        </PermissionTooltipButton>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Transfer ownership to another member to change the owner
                      of this organisation.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <PermissionTooltipButton
                  permissionKeys={[PermissionsKeys.rolesUpdate]}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </PermissionTooltipButton>
              )}
            </CardFooter>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export const ResetTwoFactorCard = () => {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Authentication</CardTitle>
        <CardDescription>
          Resetting two-factor authentication will remove all two-factor
          authentication methods for this member.
        </CardDescription>
      </CardHeader>
      <CardFooter className="justify-end">
        <Button disabled>Reset Two-Factor Auth</Button>
      </CardFooter>
    </Card>
  );
};

export const UserCard = ({ name, email, status, added }: UserCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Details</CardTitle>
        <CardDescription>
          Personal information for this member.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field>
          <FieldLabel>Name</FieldLabel>
          <Input disabled value={name} />
        </Field>
        <Field>
          <FieldLabel>Email</FieldLabel>
          <Input disabled value={email} />
        </Field>
        <Field>
          <FieldLabel>Status</FieldLabel>
          <Input disabled value={status} />
        </Field>
        <Field>
          <FieldLabel>Added</FieldLabel>
          <Input disabled value={added} />
        </Field>
      </CardContent>
    </Card>
  );
};
